/**
 * Where a project's status comes from.
 *
 * Four of the nine statuses are the system's to assign — the project reaches a
 * point in the flow and the status follows. The other five are a person's
 * decision and are never overwritten here.
 *
 * Both the API and the data migration call `deriveProjectStatus`, so a project
 * created today and one migrated from before land on the same answer.
 */

/**
 * Statuses a person sets deliberately. While a project sits on one of these,
 * the system leaves it alone: a draft stays a draft until someone publishes it,
 * and a cancelled project does not crawl back into the flow because a file was
 * attached.
 */
export const PROJECT_STATUS_MANUAL_HOLD = [
  'DRAFT',
  'RETESTING',
  'COMPLETED',
  'CANCELLED',
] as const;

/** The project fields the rule reads. All optional: callers pass what they have. */
export interface ProjectStatusFacts {
  status?: string | null;
  requirementsValidatedAt?: Date | string | null;
  requirements?: string | null;
  /** Prisma stores this as JSON: an array, a JSON string, or null. */
  attachedFiles?: unknown;
  /** Rows in RequirementDocument for this project. */
  documentCount?: number;
  /**
   * Whether the client actually has each report.
   *
   * These say **published**, not "a file exists". Generating a PDF writes
   * `reportFileKey` on the project without publishing anything, so deriving the
   * status from that field told the client "report sent" while the report was
   * still held back — the exact contradiction the deliverables work removed.
   */
  publishedReport?: boolean;
  publishedValidationReport?: boolean;
}

/** True when the client has supplied requirements in any of the three shapes. */
export function hasRequirements(facts: ProjectStatusFacts): boolean {
  if (facts.requirements && facts.requirements.trim()) return true;
  if ((facts.documentCount ?? 0) > 0) return true;

  const raw = facts.attachedFiles;
  let files: unknown = raw;
  if (typeof raw === 'string') {
    try {
      files = JSON.parse(raw);
    } catch {
      return false;
    }
  }
  return Array.isArray(files) && files.length > 0;
}

/**
 * The status this project should be in, or `null` when the system must not
 * touch it — either it is on a manual hold, or nothing about it changed.
 *
 * Read top-down: the furthest point reached wins.
 */
export function deriveProjectStatus(facts: ProjectStatusFacts): string | null {
  if (
    facts.status &&
    (PROJECT_STATUS_MANUAL_HOLD as readonly string[]).includes(facts.status)
  ) {
    return null;
  }

  let next: string;
  if (facts.publishedValidationReport) {
    next = 'VALIDATION_REPORT_SENT';
  } else if (facts.publishedReport) {
    next = 'REPORT_SENT';
  } else if (facts.requirementsValidatedAt) {
    next = 'IN_PROGRESS';
  } else if (hasRequirements(facts)) {
    next = 'VALIDATING_REQUIREMENTS';
  } else {
    next = 'WAITING_REQUIREMENTS';
  }

  return next === facts.status ? null : next;
}

/**
 * How far along the flow each status sits. CANCELLED is deliberately absent:
 * it is not a point on the line, it is stepping off it.
 */
const PROJECT_STATUS_RANK: Record<string, number> = {
  DRAFT: 0,
  WAITING_REQUIREMENTS: 1,
  VALIDATING_REQUIREMENTS: 2,
  IN_PROGRESS: 3,
  REPORT_SENT: 4,
  RETESTING: 5,
  VALIDATION_REPORT_SENT: 6,
  COMPLETED: 7,
};

export function projectStatusRank(status?: string | null): number {
  return PROJECT_STATUS_RANK[status ?? ''] ?? 0;
}

export type MilestoneState = 'completed' | 'current' | 'future';

export interface ProjectMilestone {
  id: number;
  /** The status this step stands for, so callers can label it themselves. */
  key: string;
  state: MilestoneState;
}

/**
 * The five client-facing milestones, read straight off the status.
 *
 * This used to be computed in four different places — the admin project page,
 * the client project page, and the dashboard counters — each from a different
 * mix of `requirementsValidatedAt`, `reportFileUrl` and `validationReportFileUrl`.
 * They could and did disagree. Now the status is kept current by the API, so
 * there is one input and one answer.
 */
export function projectMilestones(status?: string | null): ProjectMilestone[] {
  const rank = projectStatusRank(status);
  const cancelled = status === 'CANCELLED';

  // `reachedIsDone` separates the two kinds of status on this line. Reaching
  // REPORT_SENT means the report went out — the step is done. Reaching
  // IN_PROGRESS or RETESTING means that work is under way, so those steps stay
  // current until the project moves past them.
  const steps: { id: number; key: string; doneAt: number; reachedIsDone: boolean }[] = [
    // Step 1 is the requirements stage, which is done once they are validated
    // — the very moment the project becomes IN_PROGRESS.
    { id: 1, key: 'IN_PROGRESS', doneAt: PROJECT_STATUS_RANK['IN_PROGRESS'], reachedIsDone: true },
    // Step 2 is the testing itself, which is what IN_PROGRESS means.
    { id: 2, key: 'IN_PROGRESS', doneAt: PROJECT_STATUS_RANK['IN_PROGRESS'], reachedIsDone: false },
    { id: 3, key: 'REPORT_SENT', doneAt: PROJECT_STATUS_RANK['REPORT_SENT'], reachedIsDone: true },
    { id: 4, key: 'RETESTING', doneAt: PROJECT_STATUS_RANK['RETESTING'], reachedIsDone: false },
    {
      id: 5,
      key: 'VALIDATION_REPORT_SENT',
      doneAt: PROJECT_STATUS_RANK['VALIDATION_REPORT_SENT'],
      reachedIsDone: true,
    },
  ];

  let currentTaken = false;
  return steps.map(({ id, key, doneAt, reachedIsDone }) => {
    let state: MilestoneState;
    if (reachedIsDone ? rank >= doneAt : rank > doneAt) {
      state = 'completed';
    } else if (!currentTaken && !cancelled) {
      // The first step the project has not passed is the one it is working on.
      state = 'current';
      currentTaken = true;
    } else {
      state = 'future';
    }
    return { id, key, state };
  });
}

/**
 * Label for the first milestone, which changes as requirements move along
 * rather than being a single fixed word.
 */
export function requirementsMilestoneKey(status?: string | null): string {
  const rank = projectStatusRank(status);
  if (rank >= PROJECT_STATUS_RANK['IN_PROGRESS']) return 'REQUIREMENTS_VALIDATED';
  if (rank >= PROJECT_STATUS_RANK['VALIDATING_REQUIREMENTS'])
    return 'VALIDATING_REQUIREMENTS';
  return 'WAITING_REQUIREMENTS';
}
